import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, X, ArrowRight, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { KnowledgeGraph } from './KnowledgeGraph';

export const ComparisonSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const data = t('comparison');

  return (
    <section
      id="comparison"
      data-testid="comparison-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-zinc-950"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ delay: 0.2, type: 'spring' }}
            className="inline-flex items-center justify-center h-20 mb-2"
          >
            <span className="text-5xl font-bold text-red-500">70%</span>
          </motion.div>
          <h2
            data-testid="comparison-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
          >
            {data.headline}{' '}
            <span className="font-bold text-red-500">{data.headlineHighlight}</span>
          </h2>
          <p className="text-zinc-400 text-base lg:text-lg leading-relaxed">
            {data.subheadline}
          </p>
        </motion.div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-stretch">
          {/* Left Column (Comparisons) */}
          <div className="lg:col-span-7 xl:col-span-7 space-y-8">
            {/* Comparison Rows */}
          {data.rows.map((item, index) => (
            <motion.div
              key={item.dimension}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              data-testid={`comparison-row-${index}`}
              className="group"
            >
              <div className="relative flex flex-col sm:flex-row bg-zinc-950 border border-zinc-800 rounded-2xl shadow-sm group-hover:shadow-md group-hover:border-blue-200 transition-all duration-300">
                {/* Dimension Badge Straddling the Top Boundary */}
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-zinc-950 border border-zinc-800 text-zinc-400 text-[10px] sm:text-xs px-4 py-1.5 rounded-full uppercase tracking-widest font-bold z-10 shadow-sm whitespace-nowrap group-hover:border-blue-300 group-hover:text-blue-400 transition-colors">
                  {item.dimension}
                </div>

                {/* Traditional Approach (Pain) */}
                <div className="flex-1 p-6 pt-8 sm:p-8 bg-zinc-900/80 sm:pr-12 border-b sm:border-b-0 sm:border-r border-zinc-800 flex flex-col justify-center rounded-t-2xl sm:rounded-tr-none sm:rounded-l-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center">
                      <X className="w-3 h-3 text-zinc-400" />
                    </div>
                    <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                      {data.pain}
                    </span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed text-sm sm:text-base">
                    {item.pain}
                  </p>
                </div>
                
                {/* Center visual connector (Desktop) */}
                <div className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-zinc-950 border border-zinc-800 rounded-full items-center justify-center z-10 shadow-sm group-hover:scale-110 group-hover:text-blue-500 group-hover:border-blue-100 transition-all duration-300">
                  <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-500 transition-colors" />
                </div>

                {/* Center visual connector (Mobile) */}
                <div className="sm:hidden absolute left-1/2 top-[50%] -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-zinc-950 border border-zinc-800 rounded-full flex items-center justify-center z-10 shadow-sm rotate-90">
                  <ArrowRight className="w-3 h-3 text-zinc-300" />
                </div>

                {/* Doora Approach (Solution) */}
                <div className="flex-1 p-6 pt-8 sm:p-8 sm:pl-12 bg-blue-900/20 flex flex-col justify-center group-hover:bg-blue-900/40 transition-colors rounded-b-2xl sm:rounded-bl-none sm:rounded-r-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-blue-900/40 flex items-center justify-center shadow-[0_0_10px_rgba(59,130,246,0.2)]">
                      <Check className="w-3 h-3 text-blue-400" />
                    </div>
                    <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
                      {data.withDoora}
                    </span>
                  </div>
                  <p className="text-zinc-100 font-medium leading-relaxed text-sm sm:text-base">
                    {item.solution}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
          </div>

          {/* Right Column (Knowledge Graph) */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-5 relative mt-16 lg:mt-0">
            <motion.div
               initial={{ opacity: 0, x: 20 }}
               animate={isInView ? { opacity: 1, x: 0 } : {}}
               transition={{ duration: 0.8, delay: 0.4 }}
               className="sticky top-32 h-[calc(100vh-160px)] max-h-[850px] min-h-[695px] w-full"
            >
              <KnowledgeGraph />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
